import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../app.reducers';
import { combineLatest, Observable } from 'rxjs';
import { ActivityReport, ActivityReportFilter, ActivityReportRequest } from '../../shared/models';
import { getActivityReport, getFilter } from '../../activity-store/activity.selectors';
import { ClearActivityAction, LoadActivityAction, LoadActivityAsExcelAction } from '../../activity-store';
import { getSelectedFiscalYear, getSelectedPropertyId } from '../../../property/property-store/property.selectors';
import { filter } from 'rxjs/operators';
import { FiscalYear } from '../../../shared/models/fiscal-year';
import { SetBackToActivity } from '../../../component/component-store';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';
import { Page } from '../../../shared/models/paging';

@Component({
  selector: 'sp-activity-report',
  templateUrl: './activity-report.component.html',
  styleUrls: ['./activity-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ActivityReportComponent implements OnInit, OnDestroy {

  activityReport$: Observable<ActivityReport>;
  propertyId$: Observable<string>;
  selectedFiscalYear$: Observable<FiscalYear>;
  filter$: Observable<ActivityReportFilter>;

  filter: ActivityReportFilter;
  fiscalYear: number;
  propertyId: string;
  showFilter = true;

  private subs = new SubSink();

  constructor(private store: Store<AppState>,
    private router: Router) {
    this.activityReport$ = this.store.pipe(select(getActivityReport));
    this.propertyId$ = this.store.pipe(select(getSelectedPropertyId));
    this.selectedFiscalYear$ = this.store.pipe(select(getSelectedFiscalYear));
    this.filter$ = this.store.pipe(select(getFilter));
  }

  ngOnInit() {

    this.subs.sink = combineLatest([this.selectedFiscalYear$, this.propertyId$])
      .pipe(filter(([fy, p]) => fy != null && p != null && !fy.requireSetup ))
      .subscribe(([fiscalYear, propertyId]) => {
        if (this.propertyId != null && this.propertyId !== propertyId) {
          // property changed, no need to load this information because we'll be redirected to the dashboard.
          return;
        }

        this.propertyId = propertyId;
        this.fiscalYear = fiscalYear.year;

        const request = new ActivityReportRequest(this.propertyId, this.fiscalYear);
        this.store.dispatch(new LoadActivityAction(request));
      });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.store.dispatch(new ClearActivityAction());
  }

  onExport(activityReportFilter: ActivityReportFilter) {
    this.filter = activityReportFilter;
    const request = new ActivityReportRequest(this.propertyId, this.fiscalYear, this.filter);
    this.store.dispatch(new LoadActivityAsExcelAction(request));
  }

  onFilterChanged(activityReportFilter: ActivityReportFilter) {
    this.filter = activityReportFilter;
    const request = new ActivityReportRequest(this.propertyId, this.fiscalYear, this.filter);
    this.store.dispatch(new LoadActivityAction(request));
  }

  onHideShowFilter() {
    this.showFilter = !this.showFilter;
  }

  onPageChanged(pageNumber: number) {
    const page = new Page();
    page.pageNumber = pageNumber;
    const request = new ActivityReportRequest(this.propertyId, this.fiscalYear, this.filter, page);
    this.store.dispatch(new LoadActivityAction(request));
  }

  onViewComponentDetails(propertyComponentId: string) {
    this.store.dispatch(new SetBackToActivity(true));
    this.router.navigate(['/useraccount/components/' + propertyComponentId]);
  }

  onViewProjectDetails(projectId: string) {
    this.store.dispatch(new SetBackToActivity(true));
    this.router.navigate(['/useraccount/projects/' + projectId]);
  }
}
